import { Module, GetterTree, ActionTree, MutationTree } from 'vuex';
import { ReducerStatus, RootState } from '../types';
import { PreCheckoutState } from './types';
import { trackClarity } from '@/tracking/clarity';
import { ClarityEvent } from '@/tracking/clarity/types';
import sum from 'lodash/sum';
import sumBy from 'lodash/sumBy';

const initialState: PreCheckoutState = {
    status: ReducerStatus.INITIAL,
    showErrors: false,
    data: {
        ProductGroups: null,
        TotalPrice: { Amount: 0, Currency: '' },
    },
};

const state: PreCheckoutState = {
    ...initialState,
};

const getters: GetterTree<PreCheckoutState, RootState> = {
    getProductGroups(state) {
        return state.data.ProductGroups;
    },
    getFerryTotalPrice(state) {
        const { ProductGroups, TotalPrice } = state.data;
        const ferryProducts = (ProductGroups?.Ferry?.Addons ?? []).flatMap(
            ({ Products }) => Products
        );
        const options = ferryProducts.flatMap(({ Options }) => Options);
        const optionsIncludedInPrice = ferryProducts.flatMap(
            ({ OptionsIncludedInPrice = [] }) =>
                OptionsIncludedInPrice.map(({ Price, Value }) => Price.Amount * Number(Value))
        );

        const optionPrices = options.map(({ Price, Value }) => Price.Amount * Number(Value));

        const deductedAmount = sum(optionsIncludedInPrice);
        const optionTotalPrice = sum(optionPrices);
        return {
            Currency: TotalPrice.Currency,
            Amount: optionTotalPrice - deductedAmount,
        };
    },
    getFlightTotalPrice(state) {
        const { ProductGroups, TotalPrice } = state.data;
        const selected = (ProductGroups?.Flight?.BrandedFares ?? [])
            .flatMap((b) => b.Options)
            .filter((o) => o.IsSelected);

        return {
            Currency: TotalPrice.Currency,
            Amount: sumBy(selected, (b) => b.PriceDiff.Amount),
        };
    },
    getTotalPrice(state, getter) {
        const { Currency, Amount } = state.data.TotalPrice;
        const { getFerryTotalPrice, getFlightTotalPrice } = getter;

        return {
            Currency,
            Amount: Amount + getFerryTotalPrice.Amount + getFlightTotalPrice.Amount,
        };
    },
};

const actions: ActionTree<PreCheckoutState, RootState> = {
    setPreCheckoutLoading({ state, commit }) {
        commit('setPreCheckoutState', {
            ...state,
            status: ReducerStatus.LOADING,
        });
    },

    setPreCheckoutUpdating({ state, commit }) {
        commit('setPreCheckoutState', {
            ...state,
            status: ReducerStatus.UPDATING,
        });
    },

    setPreCheckoutLoaded({ state, commit }) {
        commit('setPreCheckoutState', {
            ...state,
            status: ReducerStatus.LOADED,
        });
    },

    setPreCheckoutSuccess({ state, commit }, payload) {
        commit('setPreCheckoutState', {
            ...state,
            status: ReducerStatus.LOADED,
            data: payload,
        });
    },

    setPreCheckoutError({ state, commit }) {
        trackClarity(ClarityEvent.PRE_CHECKOUT_INITIAL_ERROR);

        commit('setPreCheckoutState', {
            ...state,
            status: ReducerStatus.ERROR,
        });
    },

    setShowErrors({ commit }, payload) {
        commit('setShowErrors', payload);
    },

    updateProductGroup({ commit }, payload) {
        commit('setPreCheckoutState', {
            ...state,
            data: {
                ...state.data,
                ProductGroups: {
                    ...state.data.ProductGroups,
                    ...payload,
                },
            },
        });
    },
};

const mutations: MutationTree<PreCheckoutState> = {
    setPreCheckoutState(state, payload) {
        state.data = payload.data;
        state.status = payload.status;
    },
    setShowErrors(state, payload) {
        state.showErrors = payload;
    },
};

export const preCheckout: Module<PreCheckoutState, RootState> = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
